/**
 * useFomoNotifications - Custom hook for FOMO notification system
 *
 * Listens for WebSocket events from server for real-time notifications.
 * Server controls timing - client just displays what it receives.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import api from '../../services/api'
import webSocketService from '../../services/webSocketService'
import { useAuthStore } from '../../stores/authStore'
import { createLogger } from '../../utils/logger'

const logger = createLogger('useFomoNotifications')

// Default blocked routes (challenge sessions should not show FOMO)
const DEFAULT_BLOCKED_ROUTES = ['/challenge-session', '/focused-practice/.*/practice']

// Auto-dismiss timeout in milliseconds
const AUTO_DISMISS_MS = 5000

// Minimum interval between notifications on client side (safety throttle)
// This prevents showing multiple notifications too quickly if multiple server instances are running
const MIN_NOTIFICATION_INTERVAL_MS = 10000 // 10 seconds minimum between notifications

/**
 * Check if current route is blocked from showing FOMO notifications
 * @param {string} pathname - Current route pathname
 * @param {string[]} blocklist - List of blocked route patterns
 * @returns {boolean}
 */
function isRouteBlocked(pathname, blocklist = []) {
  const allBlocked = [...DEFAULT_BLOCKED_ROUTES, ...blocklist]

  return allBlocked.some((pattern) => {
    // Convert simple glob patterns to regex
    const regexPattern = pattern.replace(/\*/g, '.*')
    const regex = new RegExp(`^${regexPattern}`)
    return regex.test(pathname)
  })
}

export function useFomoNotifications() {
  const [config, setConfig] = useState(null)
  const [currentNotification, setCurrentNotification] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const location = useLocation()
  const { user } = useAuthStore()
  const dismissTimeoutRef = useRef(null)
  const configRef = useRef(null)
  const lastNotificationTimeRef = useRef(null)
  const mountedRef = useRef(false) // Start false, set true in effect
  const locationRef = useRef(location.pathname) // Track current pathname via ref
  const userRef = useRef(user) // Track current user for preference checks

  // Set mounted flag on mount, clear on unmount
  // This handles React 18 StrictMode double-mounting correctly
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // Clean up dismiss timeout on unmount
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current)
        dismissTimeoutRef.current = null
      }
    }
  }, [])

  // Keep refs in sync with state
  useEffect(() => {
    configRef.current = config
  }, [config])

  useEffect(() => {
    locationRef.current = location.pathname
  }, [location.pathname])

  useEffect(() => {
    userRef.current = user
  }, [user])

  // Clear timeout helper - no dependencies, uses only refs
  const clearDismissTimeout = useCallback(() => {
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current)
      dismissTimeoutRef.current = null
    }
  }, [])

  // Start auto-dismiss timeout - no dependencies, uses only refs
  const startDismissTimeout = useCallback(() => {
    // Clear existing timeout inline to avoid dependency
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current)
    }
    dismissTimeoutRef.current = setTimeout(() => {
      setCurrentNotification(null)
    }, AUTO_DISMISS_MS)
  }, [])

  // Dismiss current notification manually
  const dismiss = useCallback(() => {
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current)
      dismissTimeoutRef.current = null
    }
    setCurrentNotification(null)
  }, [])

  // Fetch initial config (once, for display settings before WebSocket connects)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/fomo/config/public')

        if (!mountedRef.current) return

        if (response.data.success) {
          setConfig(response.data.data)
          setError(null)
        }
      } catch (err) {
        // Silently fail - FOMO is not critical
        logger.debug('FOMO config fetch failed:', err.message)
        if (mountedRef.current) {
          setError(err.message)
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    fetchConfig()
  }, [])

  // Listen for WebSocket FOMO events - stable effect with no callback dependencies
  useEffect(() => {
    const handleFomoEvent = (data) => {
      // Prevent state updates after unmount
      if (!mountedRef.current) {
        return
      }

      // Compute effective config by merging current with incoming
      const currentConfig = configRef.current || {}
      const eventConfig = data.config || {}
      const effectiveConfig = { ...currentConfig, ...eventConfig }

      // Update config state if new config received
      if (data.config) {
        setConfig((prev) => ({ ...prev, ...data.config }))
      }

      // Check if FOMO is enabled using merged config
      if (!effectiveConfig.enabled) {
        return
      }

      // Check if user has disabled community notifications in their preferences
      if (userRef.current?.profile?.communityNotifications === false) {
        return
      }

      // Check if current route is blocked using React Router's location via ref
      const routeBlocklist = effectiveConfig.routeBlocklist || []
      if (isRouteBlocked(locationRef.current, routeBlocklist)) {
        return
      }

      // Client-side throttle: prevent notifications too close together
      const now = Date.now()
      if (
        lastNotificationTimeRef.current &&
        now - lastNotificationTimeRef.current < MIN_NOTIFICATION_INTERVAL_MS
      ) {
        logger.debug('FOMO: Throttling notification - too soon since last one')
        return
      }

      // Update last notification time
      lastNotificationTimeRef.current = now

      // Clear any existing timeout before showing new notification
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current)
      }

      // Show the notification
      setCurrentNotification(data)

      // Start auto-dismiss timeout
      dismissTimeoutRef.current = setTimeout(() => {
        setCurrentNotification(null)
      }, AUTO_DISMISS_MS)
    }

    const handleConfigUpdate = (newConfig) => {
      // Prevent state updates after unmount
      if (!mountedRef.current) return

      setConfig(newConfig)

      // If FOMO was disabled, clear any current notification
      if (!newConfig.enabled) {
        if (dismissTimeoutRef.current) {
          clearTimeout(dismissTimeoutRef.current)
          dismissTimeoutRef.current = null
        }
        setCurrentNotification(null)
      }
    }

    // Subscribe to WebSocket events
    webSocketService.on('fomo:event', handleFomoEvent)
    webSocketService.on('fomo:config', handleConfigUpdate)

    return () => {
      webSocketService.off('fomo:event', handleFomoEvent)
      webSocketService.off('fomo:config', handleConfigUpdate)
    }
  }, []) // Empty dependency array - handlers use refs for current values

  // Clear notification when entering a blocked route
  useEffect(() => {
    if (config && isRouteBlocked(location.pathname, config.routeBlocklist)) {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current)
        dismissTimeoutRef.current = null
      }
      setCurrentNotification(null)
    }
  }, [location.pathname, config])

  return {
    config,
    currentNotification,
    dismiss,
    isLoading,
    error
  }
}

export default useFomoNotifications
